import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { Clock, Calendar } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import ArticleCTA from "@/components/ArticleCTA";
import RelatedArticles from "@/components/RelatedArticles";
import ArticleValidationPoll from "@/components/ArticleValidationPoll";
import { findArticleBySlug } from "@/blog/articles";
import { CATEGORIES } from "@/blog/categories";
import { SITE_URL, PUBLISHER_NAME, PUBLISHER_LOGO, DEFAULT_OG_IMAGE } from "@/lib/seo";

/**
 * Individual article page. Resolves slug → article + language. The same component
 * is used for both /blog/:slug (CA) and /es/blog/:slug (ES). Language is taken
 * from the slug itself via findArticleBySlug, ensuring perfect SEO consistency.
 */
export default function BlogArticle() {
  const { slug } = useParams();
  const resolved = findArticleBySlug(slug);

  if (!resolved) {
    return <Navigate to="/blog" replace />;
  }

  const { article, lang } = resolved;
  const v = article[lang];
  const category = CATEGORIES[article.category];
  const langPrefix = lang === "es" ? "/es" : "";
  const blogBase = `${langPrefix}/blog`;
  const articlePath = `${blogBase}/${v.slug}`;
  const fmtDate = (iso) => {
    try {
      return new Intl.DateTimeFormat(lang === "es" ? "es-ES" : "ca-ES", {
        year: "numeric", month: "long", day: "numeric",
      }).format(new Date(iso));
    } catch {
      return iso;
    }
  };

  // Article JSON-LD (enriched with speakable and section keywords)
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: v.title,
    description: v.excerpt,
    image: [DEFAULT_OG_IMAGE],
    datePublished: article.publishedAt,
    dateModified: article.updatedAt,
    author: { "@type": "Organization", name: PUBLISHER_NAME, url: SITE_URL },
    publisher: {
      "@type": "Organization",
      name: PUBLISHER_NAME,
      logo: { "@type": "ImageObject", url: PUBLISHER_LOGO },
    },
    inLanguage: lang === "es" ? "es-ES" : "ca-ES",
    mainEntityOfPage: `${SITE_URL}${articlePath}`,
    articleSection: category ? category.label[lang] : undefined,
    keywords: v.keyword,
    // Speakable: Google Assistant / voice can read these regions aloud
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: ["h1", "[data-testid='article-hero']", "[data-testid='article-body'] > p:first-of-type"],
    },
  };

  // Optional HowTo schema — activates rich "step-by-step" snippet in Google
  const howToSchema = article.howToSteps && article.howToSteps[lang]
    ? {
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: article.howToSteps[lang].name,
        description: article.howToSteps[lang].description,
        inLanguage: lang === "es" ? "es-ES" : "ca-ES",
        totalTime: article.howToSteps[lang].totalTime,
        step: article.howToSteps[lang].steps.map((s, i) => ({
          "@type": "HowToStep",
          position: i + 1,
          name: s.name,
          text: s.text,
        })),
      }
    : null;

  // Optional FAQ schema if the article defines FAQ entries
  const faqSchema = v.faq && v.faq.length > 0
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: v.faq.map((item) => ({
          "@type": "Question",
          name: item.q,
          acceptedAnswer: { "@type": "Answer", text: item.a },
        })),
      }
    : null;

  // We can only inject one schema via the SEO component; if FAQ exists, embed
  // it as a secondary <script> within the article body below.

  // hreflang alternates between CA and ES versions of the same article
  const alternates = [
    { lang: "ca", path: `/blog/${article.ca.slug}` },
    { lang: "es", path: `/es/blog/${article.es.slug}` },
  ];

  return (
    <div className="min-h-screen bg-[#F9F8F6] text-[#0F172A] font-body" data-testid="blog-article-page">
      <SEO
        title={v.title}
        description={v.excerpt}
        path={articlePath}
        lang={lang}
        type="article"
        alternates={alternates}
        schema={articleSchema}
        article={{
          publishedTime: article.publishedAt,
          modifiedTime: article.updatedAt,
          author: PUBLISHER_NAME,
          section: category ? category.label[lang] : undefined,
        }}
      />

      {faqSchema && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      )}

      {howToSchema && (
        <script
          type="application/ld+json"
          data-testid="howto-schema"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }}
        />
      )}

      <Navbar />

      <main className="max-w-3xl mx-auto px-6 lg:px-8 py-10 sm:py-14">
        <Breadcrumbs
          items={[
            { label: "Blog", to: blogBase },
            { label: v.title.length > 60 ? v.title.slice(0, 60) + "…" : v.title },
          ]}
          homeLabel={lang === "es" ? "Inicio" : "Inici"}
        />

        <header className="mt-8 mb-10">
          {category && (
            <Link
              to={blogBase}
              className="inline-block text-[10px] uppercase tracking-[0.18em] text-[#B4C5A4] font-sans-display font-medium mb-4 hover:text-[#0B2545]"
              data-testid="article-category"
            >
              {category.label[lang]}
            </Link>
          )}
          <h1
            className="font-sans-display text-3xl sm:text-4xl lg:text-5xl text-[#0B2545] tracking-tight leading-[1.1] mb-5"
            data-testid="article-title"
          >
            {v.title}
          </h1>
          <p className="text-lg text-[#475569] leading-relaxed mb-6" data-testid="article-hero">{v.hero}</p>
          <div className="flex items-center gap-4 text-xs text-slate-400 font-sans-display border-t border-slate-200 pt-4 flex-wrap">
            <span className="inline-flex items-center gap-1.5" data-testid="article-published">
              <Calendar className="h-3.5 w-3.5" /> {fmtDate(article.publishedAt)}
            </span>
            <span className="text-slate-300">·</span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" /> {article.readingMinutes} min
            </span>
            {article.updatedAt && article.updatedAt !== article.publishedAt && (
              <>
                <span className="text-slate-300">·</span>
                <span
                  className="inline-flex items-center gap-1.5 text-[#B4C5A4]"
                  data-testid="article-updated"
                >
                  {lang === "es" ? "Actualizado" : "Actualitzat"} {fmtDate(article.updatedAt)}
                </span>
              </>
            )}
          </div>
        </header>

        <article
          className="prose-aim text-[#1f2937] leading-relaxed text-[17px] space-y-5"
          data-testid="article-body"
        >
          {v.Body ? (
            <v.Body />
          ) : (
            <>
              <p className="italic text-[#475569] bg-white border-l-2 border-[#B4C5A4] pl-4 py-3 rounded-sm text-sm">
                {lang === "es"
                  ? "🛠 Contenido en preparación. Esta página es parte de la infraestructura SEO ya publicada. El artículo completo se publicará en breve."
                  : "🛠 Contingut en preparació. Aquesta pàgina forma part de la infraestructura SEO ja publicada. L'article complet es publicarà aviat."}
              </p>
              <p>{v.body}</p>
            </>
          )}
        </article>

        {v.faq && v.faq.length > 0 && (
          <section
            className="mt-16 pt-10 border-t border-slate-200"
            data-testid="article-faq"
            aria-label={lang === "es" ? "Preguntas frecuentes" : "Preguntes freqüents"}
          >
            <p className="text-[10px] uppercase tracking-[0.24em] text-[#B4C5A4] font-sans-display font-medium mb-3">
              {lang === "es" ? "Preguntas frecuentes" : "Preguntes freqüents"}
            </p>
            <h2 className="font-sans-display text-2xl sm:text-3xl text-[#0B2545] tracking-tight leading-snug mb-8">
              {lang === "es" ? "Lo que la gente nos pregunta más" : "El que més ens pregunten"}
            </h2>
            <div className="space-y-3">
              {v.faq.map((item, i) => (
                <details
                  key={i}
                  data-testid={`faq-item-${i}`}
                  className="group bg-white border border-slate-200 rounded-lg overflow-hidden hover:border-[#0B2545]/30 transition"
                >
                  <summary className="cursor-pointer list-none p-5 flex items-start justify-between gap-4 font-sans-display text-[#0B2545] text-base sm:text-lg leading-snug">
                    <span>{item.q}</span>
                    <span
                      aria-hidden
                      className="text-2xl leading-none text-[#B4C5A4] group-open:rotate-45 transition-transform shrink-0 mt-[-2px]"
                    >
                      +
                    </span>
                  </summary>
                  <div className="px-5 pb-5 -mt-1 text-[#475569] leading-relaxed text-[15px]">
                    {item.a}
                  </div>
                </details>
              ))}
            </div>
          </section>
        )}

        <ArticleValidationPoll articleId={article.id} lang={lang} />

        <RelatedArticles
          currentId={article.id}
          currentCategory={article.category}
          lang={lang}
          langPrefix={langPrefix}
          max={3}
        />

        <ArticleCTA langPrefix={langPrefix} />
      </main>

      <Footer />
    </div>
  );
}
